import {
  Cake,
  CalendarCheck,
  CalendarIcon,
  CalendarSearch,
  Database,
  DollarSign,
  FileText,
  HandCoins,
  Hourglass,
  MapPin,
  Music,
  SquareLibrary,
  Star,
  TrendingUp,
  TriangleAlert,
  Undo2,
  Users,
} from 'lucide-react';
import type { DateRange } from 'react-day-picker';

import type { months } from '~entities/student/constants/monthsList';

// Value used in the month selector to request every month (all students).
export const ALL_MONTHS = 'all';

export type MonthSelectorValue =
  | (typeof months)[number]['value']
  | typeof ALL_MONTHS;

// Helper function to get current month date range
export const getCurrentMonthDateRange = (): DateRange => {
  const now = new Date();
  const year = now.getFullYear();
  const month = now.getMonth();

  return {
    from: new Date(year, month, 1),
    to: new Date(year, month + 1, 0),
  };
};

export type ReportCategory =
  | 'Revenue'
  | 'Analytics'
  | 'Finance'
  | 'Billing'
  | 'Enrollment'
  | 'Students';

/** Enum of all report IDs for type-safe usage across the app */
export enum ReportType {
  RevenueBySession = 'revenue-by-session',
  RevenueByClass = 'revenue-by-class',
  RevenueByLocation = 'revenue-by-location',
  RevenueOverPeriod = 'revenue-over-period',
  TransactionDetails = 'transaction-details',
  ParentOutstandingBalances = 'parent-outstanding-balances',
  OutstandingAndOverdue = 'outstanding-and-overdue',
  ScheduledUpcomingPayments = 'scheduled-upcoming-payments',
  IssuedRefunds = 'issued-refunds',
  FailedPayments = 'failed-payments',
  StripePaymentIntentsReport = 'stripe-payment-intents-report',
  TransactionCodesReport = 'transaction-codes-report',
  ActiveStudentsClassCount = 'active-students-class-count',
  BirthdayReport = 'birthday-report',
  AttendanceReport = 'attendance-report',
  StudentsByAge = 'students-by-age',
  InactiveStudents = 'inactive-students',
  ConsolidatedStudioDatabase = 'consolidated-studio-database',
  TopSpendingParents = 'top-spending-parents',
  EnrollmentsBySession = 'enrollments-by-session',
  NewVsReturningStudents = 'new-vs-returning-students',
  ClassEnrollmentsWithStudentDetails = 'class-enrollments-with-student-details',
  ClassAndEventEnrollments = 'class-and-event-enrollments',
  PaymentsBySession = 'payments-by-session',
  TrialClassReport = 'trial-class-report',
  DaysPastDue = 'days-past-due',
  PaymentsByDueDate = 'payments-by-due-date',
  DroppedClassReport = 'dropped-class-report',
  FullyBookedClassesBySession = 'fully-booked-classes-by-session',
  ClassGroupsReport = 'class-groups-report',
  ClassGenreReport = 'class-genre-report',
  SessionStudentsEnrollments = 'session-students-enrollments',
}

export interface Report {
  id: ReportType;
  name: string;
  description?: string;
  category: ReportCategory;
  icon: React.ComponentType<{ className?: string }>;
  hasDateRange?: boolean;
  hasSingleDate?: boolean;
  defaultDateRange?: DateRange;
  // Extended properties for specialized inputs
  hasLocationSelector?: boolean;
  hasPaymentStatusSelector?: boolean;
  hasDaysPastDueInput?: boolean;
  hasStripePaymentIntentsInput?: boolean;
  hasTransactionCodesLookbackInput?: boolean;
  hasMonthSelector?: boolean;
  /** When set alongside hasMonthSelector, the month selector offers an "All months" option. */
  hasAllMonthsOption?: boolean;
  /** When set, the dialog offers an Active/Inactive/Both student status filter. */
  hasBirthdayStatusFilter?: boolean;
  /** When set, the dialog offers an optional "Age As Of" date and min/max age filters. */
  hasStudentAgeFilters?: boolean;
  requiresSessionDialog?: boolean;
  requiresClassDialog?: boolean;
  requiresGroupDialog?: boolean;
  allowSessionOrDateRange?: boolean;
  hasOptionalClassFilter?: boolean;
  hasClassKeywordsInput?: boolean;
  /** When set, report is viewed on a dedicated page instead of download */
  hasViewPage?: boolean;
  viewRoute?: string;
}

export interface ReportSection {
  title: string;
  description?: string;
  category: ReportCategory;
  reports: Array<Report>;
}

export const categoryColors: Record<ReportCategory, string> = {
  Revenue: 'bg-emerald-50 text-emerald-700 border-emerald-200',
  Finance: 'bg-blue-50 text-blue-700 border-blue-200',
  Analytics: 'bg-purple-50 text-purple-700 border-purple-200',
  Billing: 'bg-orange-50 text-orange-700 border-orange-200',
  Enrollment: 'bg-yellow-50 text-yellow-700 border-yellow-200',
  Students: 'bg-pink-50 text-pink-700 border-pink-200',
};

// All report definitions
export const reports: Array<Report> = [
  // Revenue Reports
  {
    id: ReportType.RevenueBySession,
    name: 'Revenue by Session',
    description: 'Download revenue breakdown by each session',
    category: 'Revenue',
    icon: SquareLibrary,
    requiresSessionDialog: true,
  },
  {
    id: ReportType.RevenueByClass,
    name: 'Revenue by Class',
    description: 'Generate class-wise revenue data',
    category: 'Revenue',
    icon: TrendingUp,
    requiresSessionDialog: true,
    requiresClassDialog: true,
  },
  {
    id: ReportType.RevenueByLocation,
    name: 'Revenue by Location Report',
    description:
      'Export revenue report for a specific location, including all invoices linked to enrollments and events at that location',
    category: 'Revenue',
    icon: MapPin,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
    hasLocationSelector: true,
    hasPaymentStatusSelector: true,
  },
  {
    id: ReportType.RevenueOverPeriod,
    name: 'Revenue Over a Selected Time Period',
    description: 'Export total revenue product wise for a custom date range',
    category: 'Revenue',
    icon: DollarSign,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },

  // Finance Reports
  {
    id: ReportType.TransactionDetails,
    name: 'Detailed Transaction Report',
    description: 'Instantly download a CSV with every paid transaction.',
    category: 'Finance',
    icon: DollarSign,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },
  {
    id: ReportType.ParentOutstandingBalances,
    name: 'Parent Outstanding Balances',
    description: 'Download a list of all parents with unpaid balances',
    category: 'Finance',
    icon: HandCoins,
  },
  {
    id: ReportType.OutstandingAndOverdue,
    name: 'Outstanding & Overdue',
    description:
      'Every overdue invoice with parent, student, amount due, due date and days overdue',
    category: 'Finance',
    icon: Hourglass,
  },
  {
    id: ReportType.ScheduledUpcomingPayments,
    name: 'Scheduled Upcoming Payments',
    description: 'CSV of future scheduled payments by product',
    category: 'Finance',
    icon: CalendarIcon,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },
  {
    id: ReportType.IssuedRefunds,
    name: 'Issued Refunds',
    description: 'Download a report of all refunds issued',
    category: 'Finance',
    icon: Undo2,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },
  {
    id: ReportType.FailedPayments,
    name: 'Failed Payment',
    description: 'List of failed payments Parent Wise',
    category: 'Finance',
    icon: TriangleAlert,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },
  {
    id: ReportType.StripePaymentIntentsReport,
    name: 'Stripe Payment Intents Report',
    description:
      'Detailed CSV of transactions, payment status, amounts, customers, and billing information directly from Stripe',
    category: 'Finance',
    icon: DollarSign,
    hasStripePaymentIntentsInput: true,
  },
  {
    id: ReportType.TransactionCodesReport,
    name: 'Transaction Codes Report',
    description:
      'CSV of manual and bulk charges that used a transaction code. Enrollio ledger only — not Stripe payment intents, enrollment, or tuition.',
    category: 'Finance',
    icon: FileText,
    hasTransactionCodesLookbackInput: true,
  },

  // Students Reports
  {
    id: ReportType.ActiveStudentsClassCount,
    name: 'Active Students & Class Count',
    description:
      "Real-time list of every Active Student and the total classes they're enrolled with class names",
    category: 'Students',
    icon: Users,
  },
  {
    id: ReportType.BirthdayReport,
    name: 'Student Birthday Report',
    description:
      'Download a list of student birthdays for a selected month or all students',
    category: 'Students',
    icon: Cake,
    hasMonthSelector: true,
    hasAllMonthsOption: true,
    hasBirthdayStatusFilter: true,
  },
  {
    id: ReportType.AttendanceReport,
    name: 'Attendance Report',
    description:
      'View and export attendance totals, last class attended, and next scheduled class for each student',
    category: 'Students',
    icon: CalendarCheck,
    hasViewPage: true,
    viewRoute: '/payment/attendance-report',
  },
  {
    id: ReportType.StudentsByAge,
    name: 'Students by Age',
    description:
      'Every student with their DOB and age, filterable by status and age range (e.g. find everyone who will be 10 by a certain date)',
    category: 'Students',
    icon: Users,
    hasBirthdayStatusFilter: true,
    hasStudentAgeFilters: true,
  },
  {
    id: ReportType.InactiveStudents,
    name: 'Inactive Students',
    description:
      'Students not currently enrolled, with parent contact info, previous classes, CRM tags, and sibling info for re-engagement outreach',
    category: 'Students',
    icon: Users,
    hasStudentAgeFilters: true,
  },
  {
    id: ReportType.ConsolidatedStudioDatabase,
    name: 'Consolidated Report for Students and Parents',
    description: 'Download parent and student records in one file',
    category: 'Students',
    icon: Database,
  },

  // Analytics Reports
  {
    id: ReportType.TopSpendingParents,
    name: 'Top Spending Parents (Most Classes Purchased)',
    description: 'See which parents purchased the most classes',
    category: 'Analytics',
    icon: Star,
  },

  // Enrollment Reports
  {
    id: ReportType.EnrollmentsBySession,
    name: 'Enrollments by Session',
    description:
      'Shows how many students are enrolled in each session, grouped by class',
    category: 'Enrollment',
    icon: SquareLibrary,
    requiresSessionDialog: true,
  },
  {
    id: ReportType.NewVsReturningStudents,
    name: 'New vs. Returning Students',
    description:
      'Download students grouped by child with class history and new/returning classification.',
    category: 'Enrollment',
    icon: Users,
    requiresSessionDialog: true,
    hasDateRange: true,
    allowSessionOrDateRange: true,
  },
  {
    id: ReportType.ClassGroupsReport,
    name: 'Class Groups Report',
    description:
      'Download active enrollments for students in classes assigned to the selected group',
    category: 'Enrollment',
    icon: Users,
    requiresGroupDialog: true,
    hasOptionalClassFilter: true,
  },
  {
    id: ReportType.SessionStudentsEnrollments,
    name: 'Session Students & Enrollments',
    description:
      'List students in a session with all classes they are enrolled in on one row. Optionally filter to students in classes whose names contain specific keywords (e.g. Pointe, Contemporary).',
    category: 'Enrollment',
    icon: Users,
    requiresSessionDialog: true,
    hasClassKeywordsInput: true,
  },
  {
    id: ReportType.FullyBookedClassesBySession,
    name: 'Fully Booked Classes',
    description:
      'CSV of classes at full capacity (active and scheduled enrollments) for the selected session',
    category: 'Enrollment',
    icon: SquareLibrary,
    requiresSessionDialog: true,
  },
  {
    id: ReportType.ClassEnrollmentsWithStudentDetails,
    name: 'Class Enrollments with Student Details',
    description:
      'Comprehensive list of enrolled students by class, including session, instructor, student, and parent contact information.',
    category: 'Enrollment',
    icon: SquareLibrary,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },
  {
    id: ReportType.ClassAndEventEnrollments,
    name: 'Class Enrollment by Date',
    description:
      'One row per active class or event enrollment in the selected date range, sorted by most recent enrollment date.',
    category: 'Enrollment',
    icon: SquareLibrary,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },
  {
    id: ReportType.PaymentsBySession,
    name: 'Payments by Session',
    description:
      'Shows payment details for each session, including transaction amounts and status',
    category: 'Finance',
    icon: DollarSign,
    requiresSessionDialog: true,
  },
  {
    id: ReportType.TrialClassReport,
    name: 'Trial Class Report',
    description:
      'Download a report of all trial class students within a selected date range',
    category: 'Enrollment',
    icon: CalendarSearch,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },

  {
    id: ReportType.DroppedClassReport,
    name: 'Dropped Class Report',
    description:
      'List of students dropped from classes within a date range, including parent contact info, class details, and drop reason',
    category: 'Enrollment',
    icon: Users,
    hasDateRange: true,
    defaultDateRange: getCurrentMonthDateRange(),
  },

  // Enrollment Reports (Class Genre)
  {
    id: ReportType.ClassGenreReport,
    name: 'Class Genre Report',
    description:
      'Number of students enrolled per class genre (e.g. Ballet, Tap, Hip Hop) to help designate time, instructors, and anticipate needs',
    category: 'Enrollment',
    icon: Music,
    requiresSessionDialog: true,
  },

  // Billing Reports
  {
    id: ReportType.DaysPastDue,
    name: 'Days Past Due for Overdue Payments',
    description: 'CSV of overdue payments grouped by days late',
    category: 'Billing',
    icon: Hourglass,
    hasDaysPastDueInput: true,
  },
  {
    id: ReportType.PaymentsByDueDate,
    name: 'Invoice Due Date Report',
    description:
      'Enter a due date to view all invoices with that due date and their current status.',
    category: 'Billing',
    icon: CalendarIcon,
    hasSingleDate: true,
  },
];

// Group reports by category into sections
export const reportSections: Array<ReportSection> = [
  {
    title: 'Revenue Reports',
    description:
      'Track and analyze revenue across sessions, classes, and locations',
    category: 'Revenue',
    reports: reports.filter((r) => r.category === 'Revenue'),
  },
  {
    title: 'Finance Reports',
    description:
      'Transaction details, outstanding balances, and payment tracking',
    category: 'Finance',
    reports: reports.filter((r) => r.category === 'Finance'),
  },
  {
    title: 'Analytics Reports',
    description: 'Student activity and spending insights',
    category: 'Analytics',
    reports: reports.filter((r) => r.category === 'Analytics'),
  },
  {
    title: 'Enrollment Reports',
    description: 'Session-based enrollment and class information',
    category: 'Enrollment',
    reports: reports.filter((r) => r.category === 'Enrollment'),
  },
  {
    title: 'Students Reports',
    description: 'Student data and profiles',
    category: 'Students',
    reports: reports.filter((r) => r.category === 'Students'),
  },
  {
    title: 'Billing Reports',
    description: 'Overdue payments and invoice tracking',
    category: 'Billing',
    reports: reports.filter((r) => r.category === 'Billing'),
  },
];

// Dialog types for reports with input parameters
export type DialogType = ReportType | null;

// Helper function to check if a report needs a dialog
export const reportRequiresDialog = (report: Report): boolean => {
  return Boolean(
    report.hasDateRange ||
      report.hasSingleDate ||
      report.hasLocationSelector ||
      report.hasPaymentStatusSelector ||
      report.hasDaysPastDueInput ||
      report.hasStripePaymentIntentsInput ||
      report.hasTransactionCodesLookbackInput ||
      report.hasMonthSelector ||
      report.hasBirthdayStatusFilter ||
      report.hasStudentAgeFilters ||
      report.requiresSessionDialog ||
      report.requiresClassDialog ||
      report.requiresGroupDialog ||
      report.allowSessionOrDateRange ||
      report.hasClassKeywordsInput,
  );
};
